import { Trans, useLingui } from '@lingui/react/macro'
import { ArrowRightIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import * as v from 'valibot'
import { useAppForm } from '@/lib/use-app-form'
import { useMutation } from '@tanstack/react-query'
import { trpc } from '@/main'
import { toast } from '@/lib/toast'

/**
 * The static half of the contact schema lives at module scope so it is built once per process, not once
 * per render. Only `terms` needs the translated message, so that field is added by `contactSchema` inside
 * the form component (where `t` is available).
 */
const contactFields = {
  name: v.pipe(v.string(), v.minLength(1)),
  email: v.pipe(v.string(), v.email()),
  message: v.pipe(v.string(), v.minLength(1)),
}

function contactSchema(termsMessage: string) {
  return v.object({
    ...contactFields,
    terms: v.pipe(
      v.boolean(),
      v.check((val) => val, termsMessage),
    ),
  })
}

/**
 * The client half of the form's anti-abuse (server half: server/common/contact-abuse.ts): a token minted when
 * the form mounts, sent back on submit, which lets the server enforce a minimum mount → submit time. It is
 * fetched once and is simply absent until it resolves; the server only demands one outside the test stack.
 */
function useFormChallenge(): string | undefined {
  const [formToken, setFormToken] = useState<string>()
  const { mutateAsync } = useMutation(trpc.common.contactFormChallenge.mutationOptions())

  const ranRef = useRef(false)
  useEffect(() => {
    if (ranRef.current) return
    ranRef.current = true
    mutateAsync()
      .then((res) => setFormToken(res.token))
      .catch(() => undefined)
  }, [mutateAsync])

  return formToken
}

/** The contact form, inline at the landing's close (`#contact`), where every "Get in touch" on the page lands. */
export function ContactForm(props: { compact?: boolean }) {
  const { t } = useLingui()
  const { mutateAsync: sendContactForm, isSuccess } = useMutation(trpc.common.sendContactForm.mutationOptions())
  const formToken = useFormChallenge()
  const honeypotRef = useRef<HTMLInputElement>(null)

  const form = useAppForm({
    validators: {
      onChange: contactSchema(t`You must accept the privacy policy and terms of use`),
    },
    defaultValues: {
      name: '',
      email: '',
      message: '',
      terms: false,
    },
    onSubmit: async ({ value }) => {
      await sendContactForm({ ...value, hp: honeypotRef.current?.value ?? '', formToken })
      toast.success(t`Message sent successfully`)
    },
  })

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        form.handleSubmit()
      }}
      className="flex flex-col gap-3"
    >
      {/* The honeypot. Off screen rather than `display:none` (some bots skip hidden fields), non-tabbable and
          out of the accessibility tree, so a person never reaches it and a script fills it. */}
      <input
        ref={honeypotRef}
        type="text"
        name="company-website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="pointer-events-none absolute left-[-9999px] size-0 opacity-0"
      />

      <form.AppField name="name" children={(field) => <field.TextField label={t`Name`} />} />

      <form.AppField name="email" children={(field) => <field.TextField label={t`Email`} type="email" />} />

      <form.AppField name="message" children={(field) => <field.TextareaField label={t`Message`} />} />

      <form.AppField
        name="terms"
        children={(field) => (
          <field.CheckboxField
            label={
              <div>
                <Trans>
                  I accept the{' '}
                  <a
                    href="/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Terms of use
                  </a>{' '}
                  and the{' '}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Privacy Policy
                  </a>
                </Trans>
              </div>
            }
          />
        )}
      />

      <form.AppForm>
        {/* In the dialog the button spans the form; inline (`compact`) it is only as wide as its label. */}
        <form.SubmitButton
          label={t`Send`}
          disabled={isSuccess}
          className={props.compact ? 'h-9 w-fit self-start rounded-full pr-1.5! pl-5' : 'w-full'}
          icon={
            props.compact && (
              <span
                data-icon="inline-end"
                className="grid size-6 place-items-center rounded-full bg-black/80 text-white"
              >
                <ArrowRightIcon className="size-3.5" />
              </span>
            )
          }
        />
      </form.AppForm>
    </form>
  )
}
